import * as React from "react"
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress} from "@mui/material"
import {CardSeeker, IconSeeker} from "@ui-seeker/core"

import useMappersStats from "./@hooks/useMappersStats"


export default function MappersStatsPage() {
	const { isLoading, mappersData, refreshStats } = useMappersStats()

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
				<IconSeeker icon="spinner" spin sx={{ fontSize: 48, color: 'primary.main' }} />
			</Box>
		)
	}

	// Calculate totals and find top mapper
	const totals = mappersData.reduce((acc, mapper) => ({
		pendingMappings: acc.pendingMappings + (mapper.pendingMappings || 0),
		totalMappings: acc.totalMappings + (mapper.totalMappings || 0),
		last7DaysMappings: acc.last7DaysMappings + (mapper.last7DaysMappings || 0),
		totalEarnings: acc.totalEarnings + (mapper.external == 1 ? (mapper.totalEarnings || 0) : 0)
	}), { pendingMappings: 0, totalMappings: 0, last7DaysMappings: 0, totalEarnings: 0 })

	// Check if there are any external users
	const hasExternalUsers = mappersData.some(mapper => mapper.external == 1)

	// Find top mapper by total mappings
	const topMapper = mappersData.length > 0 
		? mappersData.reduce((max, mapper) => mapper.totalMappings > max.totalMappings ? mapper : max, mappersData[0])
		: null

	// Find max mappings for progress bar calculation
	const maxMappings = topMapper ? topMapper.totalMappings : 1

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
					Mappers Statistics
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Overview of all mappers performance and activity
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				<CardSeeker sx={{ flex: 1 }}>
					<Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
						<Box sx={{ 
							backgroundColor: '#e0e7ff', 
							borderRadius: 2, 
							p: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							mr: 3
						}}>
							<IconSeeker icon="users" sx={{ color: '#6366f1', fontSize: 32 }} />
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
								Total Mappers
							</Typography>
							<Typography variant="h4" sx={{ fontWeight: 700 }}>
								{mappersData.length}
							</Typography>
						</Box>
					</Box>
				</CardSeeker>

				{hasExternalUsers && (
					<CardSeeker sx={{ flex: 1 }}>
						<Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
							<Box sx={{ 
								backgroundColor: '#d1fae5', 
								borderRadius: 2, 
								p: 2,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 3
							}}>
								<IconSeeker icon="dollar-sign" sx={{ color: '#10b981', fontSize: 32 }} />
							</Box>
							<Box>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
									Total Earnings
								</Typography>
								<Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
									${totals.totalEarnings.toFixed(2)}
								</Typography>
							</Box>
						</Box>
					</CardSeeker>
				)}

				{topMapper && (
					<CardSeeker sx={{ flex: 1, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
						<Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
							<Box sx={{ 
								backgroundColor: '#fff', 
								borderRadius: 2, 
								p: 2,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 3
							}}>
								<IconSeeker icon="crown" sx={{ color: '#f59e0b', fontSize: 32 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="body2" sx={{ color: '#92400e', mb: 0.5, fontWeight: 600 }}>
									Top Mapper
								</Typography>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#78350f', mb: 0.5 }}>
									{topMapper.username.split('@')[0]}
								</Typography>
								<Typography variant="caption" sx={{ color: '#92400e' }}>
									{topMapper.totalMappings.toLocaleString()} mappings
								</Typography>
							</Box>
						</Box>
					</CardSeeker>
				)}
			</Box>

			<CardSeeker>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow sx={{ backgroundColor: '#f9fafb' }}>
								<TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>Pending</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>Total Completed</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>Last 7 Days</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>Daily Average</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>Earnings</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mappersData.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center" sx={{ py: 4 }}>
										<Typography variant="body2" color="text.secondary">
											No mappers data available
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								mappersData.map((mapper, index) => {
									const isTopMapper = topMapper && mapper.username === topMapper.username
									return (
										<TableRow 
											key={index}
											sx={{ 
												'&:hover': { backgroundColor: '#f9fafb' },
												'&:last-child td': { border: 0 },
												backgroundColor: isTopMapper ? '#fef3c7' : 'transparent'
											}}
										>
											<TableCell>
												<Box sx={{ display: 'flex', alignItems: 'center' }}>
													<Box sx={{ 
														width: 32, 
														height: 32, 
														borderRadius: '50%', 
														backgroundColor: isTopMapper ? '#fde68a' : '#e0e7ff',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														mr: 2,
														position: 'relative'
													}}>
														<IconSeeker 
															icon="user" 
															sx={{ fontSize: 16, color: isTopMapper ? '#f59e0b' : '#6366f1' }} 
														/>
														{isTopMapper && (
															<Box sx={{ 
																position: 'absolute', 
																top: -4, 
																right: -4,
																backgroundColor: '#fff',
																borderRadius: '50%',
																width: 16,
																height: 16,
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
															}}>
																<IconSeeker icon="crown" sx={{ fontSize: 10, color: '#f59e0b' }} />
															</Box>
														)}
													</Box>
													<Typography variant="body2" sx={{ fontWeight: isTopMapper ? 600 : 500 }}>
														{mapper.username}
													</Typography>
												</Box>
											</TableCell>
										<TableCell align="right">
											{mapper.pendingMappings > 0 ? (
												<Chip 
													label={mapper.pendingMappings.toLocaleString()} 
													size="small"
													sx={{ 
														backgroundColor: '#fef3c7',
														color: '#92400e',
														fontWeight: 600
													}}
												/>
											) : (
												<Typography variant="body2" color="text.secondary">
													0
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
												<Box sx={{ flex: 1, minWidth: 100 }}>
													<LinearProgress 
														variant="determinate" 
														value={(mapper.totalMappings / maxMappings) * 100}
														sx={{ 
															height: 8, 
															borderRadius: 1,
															backgroundColor: '#e5e7eb',
															'& .MuiLinearProgress-bar': {
																backgroundColor: isTopMapper ? '#f59e0b' : '#3b82f6',
																borderRadius: 1
															}
														}}
													/>
												</Box>
												<Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
													{mapper.totalMappings.toLocaleString()}
												</Typography>
											</Box>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2">
												{mapper.last7DaysMappings.toLocaleString()}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2">
												{mapper.avgMappingsPerDay.toFixed(1)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											{mapper.external == 1 ? (
												<Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
													${mapper.totalEarnings.toFixed(2)}
												</Typography>
											) : (
												<Typography variant="body2" color="text.secondary">
													-
												</Typography>
											)}
										</TableCell>
									</TableRow>
								)
								})
							)}
							{mappersData.length > 0 && (
								<TableRow sx={{ backgroundColor: '#f3f4f6' }}>
									<TableCell sx={{ fontWeight: 700 }}>
										TOTAL
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{totals.pendingMappings.toLocaleString()}
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{totals.totalMappings.toLocaleString()}
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{totals.last7DaysMappings.toLocaleString()}
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{mappersData.length > 0 ? (totals.last7DaysMappings / mappersData.length / 7).toFixed(1) : '0.0'}
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700, color: hasExternalUsers ? '#10b981' : 'text.secondary' }}>
										{hasExternalUsers ? `$${totals.totalEarnings.toFixed(2)}` : '-'}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</CardSeeker>
		</Box>
	)
}
